import classnames from 'classnames';
import * as React from 'react';

import patternBgImage from '../assets/images/pattern-bg.png';
import {
	chatbotClientVersionOverride,
	chatbotEnvironmentOverride,
	isAutoScrollMode,
	isQAMode,
	isReadOnlyMode,
	isTestMode,
	mvtConfig,
	shouldLogResponseMetaData,
} from '../globals';
import useChat from '../hooks/useChat';
import useBottomSheetStore from '../store/bottomSheet';
import useDrawerStore from '../store/drawer';
import {
	ChatMode,
	ChatWindowStatusTypes,
	LiveChatStatus,
	useChatState,
} from '../store/index';
import ChatForm from './ChatForm';
import ChatHistory from './ChatHistory';
import ChatWindowHeader from './ChatWindowHeader';
import FatalError from './FatalError';
import LiveChatStatusBar from './LiveChatStatusBar';
import LoadingSpinner from './LoadingSpinner';
import Overlay from './Overlay';
import ResponseMetaData from './ResponseMetaData';
import Results from './Results';
import Snackbar from './Snackbar';
import BottomSheet from './bottom-sheet/BottomSheet';
import Drawer from './drawer/Drawer';

export type ChatWindowVariants = 'lozenge';

interface ChatWindowProps {
	toggleChatWindow: (evt: React.MouseEvent) => void;
	refreshChat: (evt: React.MouseEvent) => void;
	status?: ChatWindowStatusTypes;
	variant?: ChatWindowVariants;
}

export const ChatWindow: React.FC<
	ChatWindowProps & React.HTMLProps<HTMLDivElement>
> = ({ toggleChatWindow, refreshChat, status, variant, className }) => {
	const { chatWindowHeaderTitle } = mvtConfig;
	const globalState = useChatState();
	const {
		sessionId,
		results,
		responseMetaData,
		chatHistory,
		showTypingIndicator,
		chatMode,
		liveChatStatus,
		liveChatAgent,
		liveChatActivationData,
		showLiveChatAutoDisconnectNotice,
		lastPageUpdate,
	} = globalState;

	const { refreshChatSession } = useChat();
	const bottomSheetStore = useBottomSheetStore();
	const BottomSheetContent = bottomSheetStore.content;
	const drawerStore = useDrawerStore();
	const DrawerContent = drawerStore.content;

	const [conversationScrollerWidth, setConversationScrollerWidth] =
		React.useState(0);
	const [shouldRenderForm, setShouldRenderForm] = React.useState(true);
	const [chatHistoryPaddingBottom, setChatHistoryPaddingBottom] =
		React.useState(0);
	const [
		liveChatConnectionErrorIsVisible,
		setLiveChatConnectionErrorIsVisible,
	] = React.useState(false);
	const [liveChatStatusBarIsVisible, setLiveChatStatusBarIsVisible] =
		React.useState(false);
	const [headerDividerIsVisible, setHeaderDividerIsVisible] =
		React.useState(true);
	const [overlayIsVisible, setOverlayIsVisible] = React.useState(false);

	const snackbarRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		let shouldRender = true;

		if (chatMode === ChatMode.LiveChat) {
			if (
				liveChatStatus === LiveChatStatus.Connecting ||
				liveChatStatus === LiveChatStatus.ConnectionError ||
				liveChatStatus === LiveChatStatus.Transferring
			) {
				shouldRender = false;
			}
		}

		setShouldRenderForm(shouldRender);
	}, [chatMode, liveChatStatus]);

	React.useEffect(() => {
		if (
			(liveChatStatus === LiveChatStatus.Connecting ||
				liveChatStatus === LiveChatStatus.Transferring) &&
			snackbarRef.current
		) {
			setChatHistoryPaddingBottom(snackbarRef.current.offsetHeight);
		} else {
			setChatHistoryPaddingBottom(0);
		}
	}, [liveChatStatus]);

	React.useEffect(() => {
		setLiveChatConnectionErrorIsVisible(
			liveChatStatus === LiveChatStatus.ConnectionError ? true : false
		);
	}, [liveChatStatus]);

	React.useEffect(() => {
		setLiveChatStatusBarIsVisible(
			chatMode === ChatMode.LiveChat ? true : false
		);
	}, [chatMode]);

	React.useEffect(() => {
		setHeaderDividerIsVisible(
			liveChatStatusBarIsVisible || results ? false : true
		);
	}, [liveChatStatus, liveChatStatusBarIsVisible, results]);

	React.useEffect(() => {
		setOverlayIsVisible(
			liveChatConnectionErrorIsVisible || showLiveChatAutoDisconnectNotice
				? true
				: false
		);
	}, [liveChatConnectionErrorIsVisible, showLiveChatAutoDisconnectNotice]);

	return (
		<div
			data-testid="chat-window"
			className={classnames(
				'c-chat-window shadow-16dp md:rounded-md overflow-hidden bg-white',
				{
					'c-chat-window--lozenge': variant && variant === 'lozenge',
				},
				className
			)}
		>
			<Drawer
				options={drawerStore.options}
				isOpen={drawerStore.isOpen}
				onClose={() => {
					drawerStore.onClose();
				}}
			>
				{DrawerContent && <DrawerContent />}
			</Drawer>

			<BottomSheet
				className="z-50"
				options={bottomSheetStore.options}
				isOpen={bottomSheetStore.isOpen}
				onClose={() => {
					bottomSheetStore.onClose();
				}}
			>
				{BottomSheetContent && <BottomSheetContent />}
			</BottomSheet>

			{/* BG Image */}
			<div
				style={{
					backgroundImage: `url(${patternBgImage})`,
					backgroundSize: '208px',
				}}
				className="bg-gray-10 absolute inset-0 block bg-repeat"
			/>

			<div
				ref={(node) =>
					node &&
					(drawerStore.isOpen || bottomSheetStore.isOpen
						? node.setAttribute('inert', '')
						: node.removeAttribute('inert'))
				}
				className="relative flex flex-col flex-grow"
			>
				<ChatWindowHeader
					clickCloseCallback={toggleChatWindow}
					showDivider={headerDividerIsVisible}
				>
					{chatWindowHeaderTitle ? (
						<React.Fragment>{chatWindowHeaderTitle}</React.Fragment>
					) : (
						<React.Fragment>Hello, how can I help?</React.Fragment>
					)}
				</ChatWindowHeader>

				{isTestMode && (
					<div
						data-testid="debug-ribbon-test-mode"
						className="text-caption text-center text-black"
						style={{ background: '#ffaaff' }}
					>
						Test Mode
					</div>
				)}

				{isQAMode && (
					<div
						data-testid="debug-ribbon-qa-mode"
						className="text-caption text-center text-black"
						style={{ background: '#aaffaa' }}
					>
						QA Mode
					</div>
				)}

				{chatbotEnvironmentOverride && (
					<div
						data-testid="debug-ribbon-environment"
						className="text-caption text-center text-black"
						style={{ background: '#47BADE' }}
					>
						Environment: <strong>{chatbotEnvironmentOverride}</strong>
					</div>
				)}

				{chatbotClientVersionOverride && (
					<div
						data-testid="debug-ribbon-client-version"
						className="text-caption text-center text-white"
						style={{ background: '#9B5BF5' }}
					>
						Client Version: <strong>{chatbotClientVersionOverride}</strong>
					</div>
				)}

				{isReadOnlyMode && (
					// TODO: Convert to a separate component?
					<div
						data-testid="debug-ribbon-read-only"
						className="text-caption text-center text-black"
						style={{ background: '#ffa878' }}
					>
						Read Only Mode.{' '}
						<button
							type="button"
							className="text-link p-0 text-xs bg-transparent cursor-pointer"
							onClick={refreshChatSession}
						>
							Refresh
						</button>
						<div>
							Session: <strong>{sessionId}</strong>
						</div>
						{lastPageUpdate && (
							<a href={lastPageUpdate.pageUrl} target="_blank" rel="noreferrer">
								View current page
							</a>
						)}
					</div>
				)}

				{shouldLogResponseMetaData && responseMetaData && (
					<ResponseMetaData responseMetaData={responseMetaData} />
				)}

				{status === ChatWindowStatusTypes.Error ? (
					<FatalError reloadCallback={refreshChat} />
				) : (
					<div className="relative flex flex-col flex-grow">
						{status === ChatWindowStatusTypes.Loading && (
							<LoadingSpinner className="z-30" />
						)}

						{overlayIsVisible && !isReadOnlyMode && (
							<Overlay
								data-testid="chat-window-overlay"
								opacity={50}
								className="z-20"
							/>
						)}

						{liveChatStatusBarIsVisible && (
							<LiveChatStatusBar
								status={liveChatStatus}
								agent={liveChatAgent}
								liveChatActivationData={liveChatActivationData}
								showAutoDisconnectNotice={
									isReadOnlyMode ? false : showLiveChatAutoDisconnectNotice
								}
								isReadOnly={isReadOnlyMode}
								className="z-30"
							/>
						)}

						{results && (
							<Results resultsData={results} className="relative z-10" />
						)}

						<div
							ref={(node) =>
								node &&
								(overlayIsVisible
									? node.setAttribute('inert', '')
									: node.removeAttribute('inert'))
							}
							className="flex flex-col flex-grow"
						>
							{chatHistory && (
								<ChatHistory
									history={chatHistory}
									showTypingIndicator={showTypingIndicator}
									setConversationScrollerWidth={(width) =>
										setConversationScrollerWidth(width)
									}
									paddingBottomOffset={chatHistoryPaddingBottom}
									shouldAutoScroll={isAutoScrollMode}
								/>
							)}

							{(liveChatStatus === LiveChatStatus.Connecting ||
								liveChatStatus === LiveChatStatus.Transferring) && (
								<Snackbar
									ref={snackbarRef}
									containerWidth={conversationScrollerWidth}
								>
									{liveChatStatus === LiveChatStatus.Connecting && (
										<React.Fragment>
											<p>
												<strong>Won&apos;t be a moment</strong>
											</p>
											<p>
												You&apos;re in the queue, so please bear with us and
												we&apos;ll be with you in a jiffy.
											</p>
										</React.Fragment>
									)}
									{liveChatStatus === LiveChatStatus.Transferring && (
										<React.Fragment>
											<p>
												<strong>We&apos;re transferring you now&hellip;</strong>
											</p>
											<p>
												A member of our customer service team will be with you
												soon.
											</p>
										</React.Fragment>
									)}
								</Snackbar>
							)}

							{!isReadOnlyMode && shouldRenderForm && (
								<div
									className="border-gray-30 relative overflow-hidden border border-b-0 border-l-0 border-r-0 border-solid"
									style={{ height: '60px' }}
								>
									{/* TODO: Apply focus to the form input when the chatwindow is opened on desktop devices (on mount) */}
									<ChatForm
										maxCharacterLength={
											chatMode === ChatMode.ChatBot ? 256 : undefined
										}
										isReadOnly={isReadOnlyMode}
									/>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ChatWindow;
